import { useState } from "react";
import { DetailField } from "./DetailField";
import { Select } from "./Field";
import { DialogActions, Modal } from "./Modal";
import type { Account } from "../data/types";

/**
 * The Account select on a request, with a confirmation step.
 *
 * Re-filing a request under another customer is not like the other fields in
 * this aside. It changes which portal shows it — the old customer loses sight
 * of it and the new one gains it, comments and all — and it is deliberately
 * not written to the Activity feed, so the screen is the only place the move
 * is ever announced. Hence the dialog: it names both customers, and says what
 * will be dropped, before anything is sent.
 */
export function MoveAccountField({
  accounts,
  accountId,
  accountName,
  milestoneCount,
  disabled,
  onMove,
}: {
  accounts: Account[];
  accountId: string;
  accountName: string;
  /** Milestone links on this request today; all of them are dropped by a move. */
  milestoneCount: number;
  disabled?: boolean;
  onMove: (accountId: string) => void;
}) {
  const [target, setTarget] = useState<Account | null>(null);

  return (
    <DetailField label="Account">
      <Select
        aria-label="Account"
        value={accountId}
        disabled={disabled}
        onChange={(e) => {
          const next = accounts.find((a) => a.id === e.target.value);
          // Re-selecting the current account is not a move; the select is
          // already showing it, so there is nothing to confirm or to send.
          if (next && next.id !== accountId) setTarget(next);
        }}
      >
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </Select>

      <Modal
        open={target !== null}
        onClose={() => setTarget(null)}
        title="Move this request?"
        footer={
          <DialogActions
            onCancel={() => setTarget(null)}
            onConfirm={() => {
              if (target) onMove(target.id);
              setTarget(null);
            }}
            confirmLabel="Move request"
          />
        }
      >
        <p className="text-sm text-[var(--text-1)]">
          This moves the request from <strong>{accountName}</strong> to <strong>{target?.name}</strong>.
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-[var(--text-2)]">
          <li>
            {accountName} loses it from their portal; {target?.name} sees it, including any customer-visible comments already on it.
          </li>
          {milestoneCount > 0 && (
            <li>
              Its {milestoneCount === 1 ? "milestone link is" : `${milestoneCount} milestone links are`} dropped — milestones belong to one
              customer.
            </li>
          )}
          <li>Nothing is written to the Activity feed.</li>
        </ul>
      </Modal>
    </DetailField>
  );
}
